import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Link2 } from "lucide-react";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function QuickEstimateLink({ open, onOpenChange }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleSend = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setSending(true);
    try {
      const token = getSessionToken();
      await axios.post(`${API}/messages/quick-quote`, {
        phone: digits,
        name: name.trim() || null,
        utm_source: "manual"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Estimate link sent to ${phone}!`);
      setPhone("");
      setName("");
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="quick-estimate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Link2 className="w-5 h-5 text-[#207024]" />
            Send Estimate Link
          </DialogTitle>
          <DialogDescription>
            Text someone a link to get an instant estimate
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label className="font-semibold text-zinc-700">Phone Number *</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(951) 555-1234"
              className="h-12 mt-1"
              data-testid="quick-estimate-phone"
            />
          </div>
          
          <div>
            <Label className="font-semibold text-zinc-700">Name <span className="text-zinc-400 font-normal">(optional)</span></Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Their first name"
              className="h-12 mt-1"
              data-testid="quick-estimate-name"
            />
          </div>

          <div className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-600">
            <p className="font-medium text-zinc-700 mb-1">Preview:</p>
            <p>Hey{name.trim() ? ` ${name.trim()}` : ""}! This is Brett from ScreenzZ Window Screen Repair.</p>
            <p className="mt-1">Here's a link to get a quick estimate on your screens...</p>
            <p className="mt-1 text-[#207024] font-medium">screenzz.com/quote</p>
          </div>

          <button
            onClick={handleSend}
            disabled={sending || phone.replace(/\D/g, "").length !== 10}
            className="w-full h-12 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="quick-estimate-send"
          >
            <Send className="w-4 h-4" />
            {sending ? "Sending..." : "Send Estimate Link"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
